import { useCallback, useEffect, useRef } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

// The provider loads Google's script on mount, so a fast submit (or slow mobile network) can arrive
// before executeRecaptcha exists. Wait this long for it before treating reCAPTCHA as unavailable.
const RECAPTCHA_READY_TIMEOUT_MS = 5000
const RECAPTCHA_READY_POLL_MS = 250

interface UseReCaptchaV3 {
  getReCaptchaToken: (action: string) => Promise<string | undefined>
}

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

function useReCaptchaV3(): UseReCaptchaV3 {
  const { executeRecaptcha } = useGoogleReCaptcha()
  // Read through a ref: an in-flight call must see the function once the script loads, not the
  // undefined captured by the render that started it.
  const executeRecaptchaRef = useRef(executeRecaptcha)

  useEffect(() => {
    executeRecaptchaRef.current = executeRecaptcha
  }, [executeRecaptcha])

  const getReCaptchaToken = useCallback(async (action: string): Promise<string | undefined> => {
    const waitForExecute = async (remainingMs: number): Promise<typeof executeRecaptcha> => {
      if (executeRecaptchaRef.current || remainingMs <= 0) return executeRecaptchaRef.current
      await wait(RECAPTCHA_READY_POLL_MS)
      return waitForExecute(remainingMs - RECAPTCHA_READY_POLL_MS)
    }

    const execute = await waitForExecute(RECAPTCHA_READY_TIMEOUT_MS)
    if (!execute) return undefined

    try {
      return await execute(action)
    } catch {
      // Outside a provider the library's default executeRecaptcha throws; a blocked or broken
      // script can reject. Either way there is no token.
      return undefined
    }
  }, [])

  return { getReCaptchaToken }
}

export default useReCaptchaV3
